import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ErroreApi, chiama } from '../api/cliente.js';
import { type Ordine, useCorreggiTestata } from '../api/ordini.js';
import { useDestinazioniCliente } from '../api/ordini-scrittura.js';
import { Modale } from '../componenti/Modale.js';
import { SchedaDestinazione, voceDestinazione } from '../componenti/SchedaDestinazione.js';
import { giornoItaliano } from '../giorni.js';
import { t } from '../i18n/lingua.js';

/**
 * Correggere la testata di un ordine.
 *
 * Nella 3.0 non si poteva: una volta creato, l'ordine cambiava solo
 * avanzando. Nel v2 si corregge di continuo — la consegna richiesta 616
 * volte nel 2026, la data dell'ordine 131, la destinazione 86.
 *
 * Quali campi si aprono lo decide il servizio, stato per stato, con la regola
 * del v2: questa finestra mostra quelli che arrivano in `editable` e nient'altro.
 * Salvare non manda niente a Business Central né ai vettori.
 */

type Tipo = 'testo' | 'data' | 'nota' | 'destinazione' | 'pagamento' | 'banca' | 'vettore';

const CAMPI: Record<string, { etichetta: string; tipo: Tipo }> = {
  orderNumber: { etichetta: t('Numero d’ordine del cliente'), tipo: 'testo' },
  orderDate: { etichetta: t('Data dell’ordine'), tipo: 'data' },
  addressId: { etichetta: t('Destinazione della merce'), tipo: 'destinazione' },
  deliveryDateRequested: { etichetta: t('Consegna richiesta dal cliente'), tipo: 'data' },
  paymentTypeId: { etichetta: t('Tipo di pagamento'), tipo: 'pagamento' },
  bankId: { etichetta: t('Banca'), tipo: 'banca' },
  carrierId: { etichetta: t('Vettore'), tipo: 'vettore' },
  estimatedDepartureDate: { etichetta: t('Partenza stimata'), tipo: 'data' },
  goodsReadyShippingDate: { etichetta: t('Merce pronta il'), tipo: 'data' },
  goodsShippedDate: { etichetta: t('Merce partita il'), tipo: 'data' },
  shipCarrier: { etichetta: t('Nave'), tipo: 'testo' },
  containerShipmentNumber: { etichetta: t('Container / spedizione'), tipo: 'testo' },
  estimatedArrivalDate: { etichetta: t('Arrivo stimato'), tipo: 'data' },
  estimatedDeliveryDate: { etichetta: t('Consegna stimata'), tipo: 'data' },
  arrivalDate: { etichetta: t('Data di arrivo'), tipo: 'data' },
  deliveryDate: { etichetta: t('Data di consegna'), tipo: 'data' },
  invoiceNumberSpa: { etichetta: t('Numero fattura SPA'), tipo: 'testo' },
  invoiceDateSpa: { etichetta: t('Data fattura SPA'), tipo: 'data' },
  invoiceNumberInc: { etichetta: t('Numero fattura Inc'), tipo: 'testo' },
  invoiceDateInc: { etichetta: t('Data fattura Inc'), tipo: 'data' },
  notes: { etichetta: t('Annotazioni'), tipo: 'nota' },
  shipmentNotes: { etichetta: t('Note di spedizione'), tipo: 'nota' },
};

/** Il valore di partenza, come lo vuole un campo del modulo. */
function iniziale(ordine: Ordine, campo: string): string {
  const v = (ordine as unknown as Record<string, unknown>)[campo];
  if (v === null || v === undefined) return '';
  if (CAMPI[campo]?.tipo === 'data') return giornoItaliano(String(v));
  return String(v);
}

export function ModaleTestataOrdine({
  ordine,
  onChiudi,
}: {
  ordine: Ordine;
  onChiudi: () => void;
}) {
  const salva = useCorreggiTestata(ordine.id);
  const campi = Object.keys(CAMPI).filter((c) => ordine.editable.includes(c));
  const [valori, setValori] = useState<Record<string, string>>(() =>
    Object.fromEntries(campi.map((c) => [c, iniziale(ordine, c)])),
  );

  const destinazioni = useDestinazioniCliente(
    campi.includes('addressId') ? ordine.customerId : null,
  );
  const opzioni = useQuery({
    queryKey: ['opzioni-ordine'],
    queryFn: () =>
      chiama<{ options: Record<string, { id: string | number; label: string }[]> }>(
        '/api/orders/filters',
      ),
    staleTime: 10 * 60_000,
  });
  const banche = useQuery({
    queryKey: ['banche'],
    queryFn: () =>
      chiama<{ rows: { id: number; name: string }[] }>('/api/reference-data/banks?size=100'),
    staleTime: 10 * 60_000,
    enabled: campi.includes('bankId'),
  });

  const destinazioneScelta = destinazioni.data?.destinations.find(
    (d) => String(d.id) === valori.addressId,
  );

  const cambiati = campi.filter((c) => valori[c] !== iniziale(ordine, c));
  const errore = salva.error instanceof ErroreApi ? salva.error.message : null;

  const conferma = () => {
    const corpo: Record<string, unknown> = {};
    for (const c of cambiati) {
      const v = valori[c]!.trim();
      const tipo = CAMPI[c]!.tipo;
      if (['destinazione', 'pagamento', 'banca', 'vettore'].includes(tipo)) {
        corpo[c] = v === '' ? null : Number(v);
      } else {
        corpo[c] = v === '' ? null : v;
      }
    }
    salva.mutate(corpo, { onSuccess: onChiudi });
  };

  const scelte = (tipo: Tipo): { id: string | number; label: string }[] => {
    if (tipo === 'destinazione') {
      return (destinazioni.data?.destinations ?? []).map((d) => ({ id: d.id, label: voceDestinazione(d) }));
    }
    if (tipo === 'pagamento') return opzioni.data?.options.paymentTypes ?? [];
    if (tipo === 'vettore') return opzioni.data?.options.carriers ?? [];
    if (tipo === 'banca') return (banche.data?.rows ?? []).map((b) => ({ id: b.id, label: b.name }));
    return [];
  };

  return (
    <Modale
      titolo={t('Correggi la testata')}
      occhiello={`${ordine.orderNumber} · ${ordine.state}`}
      nota={t('Si aprono solo i campi che in questo stato si possono cambiare. Salvare non manda niente a Business Central né ai vettori.')}
      larga
      inCorso={salva.isPending}
      onChiudi={onChiudi}
      azioni={
        <>
          <button className="btn" onClick={onChiudi} disabled={salva.isPending}>
            {t('Annulla')}
          </button>
          <button
            className="btn btn-primary"
            onClick={conferma}
            disabled={cambiati.length === 0 || salva.isPending}
          >
            {salva.isPending ? t('Salvo…') : t('Salva')}
          </button>
        </>
      }
    >
      {errore && (
        <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
          <span className="avviso-segno">▲</span>
          <span>{errore}</span>
        </div>
      )}

      <div className="fields">
        {campi.map((c) => {
          const { etichetta, tipo } = CAMPI[c]!;
          const id = `tt-${c}`;
          const cambia = (v: string) => setValori((x) => ({ ...x, [c]: v }));
          return (
            <div
              className={tipo === 'nota' ? 'field span-2' : 'field'}
              style={tipo === 'destinazione' ? { gridColumn: '1 / -1' } : undefined}
              key={c}
            >
              <label htmlFor={id}>{etichetta}</label>
              {tipo === 'testo' && (
                <input id={id} className="input" value={valori[c]} onChange={(e) => cambia(e.target.value)} />
              )}
              {tipo === 'data' && (
                <input
                  id={id}
                  className="input"
                  type="date"
                  value={valori[c]}
                  onChange={(e) => cambia(e.target.value)}
                />
              )}
              {tipo === 'nota' && (
                <textarea
                  id={id}
                  className="input"
                  rows={3}
                  value={valori[c]}
                  onChange={(e) => cambia(e.target.value)}
                />
              )}
              {['destinazione', 'pagamento', 'banca', 'vettore'].includes(tipo) && (
                <select id={id} className="select" value={valori[c]} onChange={(e) => cambia(e.target.value)}>
                  {tipo === 'vettore' && <option value="">{t('Nessuno')}</option>}
                  {/* un ordine nato senza destinazione: si può dargliene una */}
                  {tipo === 'destinazione' && valori[c] === '' && (
                    <option value="">{t('Nessuna: in BC va l’indirizzo del cliente')}</option>
                  )}
                  {/* il valore attuale resta scelto anche prima che arrivi l'elenco */}
                  {valori[c] !== '' && !scelte(tipo).some((o) => String(o.id) === valori[c]) && (
                    <option value={valori[c]}>…</option>
                  )}
                  {scelte(tipo).map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              )}
              {tipo === 'destinazione' && destinazioneScelta && (
                <SchedaDestinazione destinazione={destinazioneScelta} />
              )}
            </div>
          );
        })}
      </div>
    </Modale>
  );
}
